<script setup lang="ts">
import CanvasRenderer from '@/components/CanvasRenderer.vue'
import { useCanvasStore } from '@/store/canvas'
import { computed, onMounted, ref } from 'vue'
import { DateTime } from 'luxon'
import { useDisplay } from 'vuetify'
import { useInstanceStore } from '@/store/backendInstances'
import CameraSpeedChart from '@/composables/charts/quickCharts/CameraSpeedChart.vue'
import GenericInfluxChart from '@/composables/charts/GenericInfluxChart.vue'
import GlobalGridScoreChart from '@/composables/charts/quickCharts/GlobalGridScoreChart.vue'
import { GeneralChartTimeSetup } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup'
import { IRInfluxChartBuilder } from '@/composables/charts/influx-charts/chart-builders/IRChartBuilder'
import { CHART_TYPES } from '@/composables/charts/influx-charts/chart-builders/CHART_TYPES'
import { RegionInfluxChartBuilder } from '@/composables/charts/influx-charts/chart-builders/RegionChartBuilder'

const canvasStore = useCanvasStore()
const { mobile } = useDisplay()

const timestamp = ref(DateTime.now().startOf('second'))

const instanceStore = useInstanceStore()

const baseCanvas = computed(() => {
  return canvasStore.getBaseCanvas()
})

const canSeeBucketScores = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.buckets'])
})
const canSeeRegionData = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.regions'])
})
const canSeeSpeedData = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.speed'])
})
const canSeeImageQualityData = computed(() => {
  return instanceStore.hasAnyPermission(['data.view', 'data.view.imageQuality'])
})

const allStandingPigs = ['piglet_standing', 'pig_standing', 'sow_standing']
const allNonStandingPigs = [
  'piglet',
  'piglet_lying',
  'piglet_sitting',
  'pig',
  'pig_sitting',
  'pig_lying',
  'sow_lying_belly',
  'sow_lying_side',
  'sow_sitting',
]

const isVerticalBarn = computed(() => {
  return baseCanvas.value?.heightRatio < 2.5
})

onMounted(() => {
  timestamp.value = DateTime.now().startOf('second')
  canvasStore.keepBaseCanvasLoaded()
})
</script>

<template>
  <v-row class="ma-0">
    <v-col :cols="12">
      <!-- we need overflow-visible here, because when opening the plugins they are overflowing the canvas -->
      <v-layout-card
        class="pa-0 overflow-visible"
        :min-height="isVerticalBarn ? '50vh' : '400px'"
        height="100%"
        style="z-index: 1"
      >
        <CanvasRenderer
          v-if="baseCanvas"
          :full-height="!mobile"
          :disable-zoom-buttons="false"
          :override-canvas="baseCanvas"
          :disable-element-picker="false"
          :disable-time-jumper="false"
          :disable-navigation="false"
          :draggable="!mobile"
          :pinchable="mobile"
          :scrollable="false"
          :ctrl-scrollable="!mobile"
          :shrink-all-overlays-by-default="true"
          :timestamp="timestamp"
        />
      </v-layout-card>
    </v-col>

    <v-col
      v-if="canSeeSpeedData"
      :cols="12"
    >
      <CameraSpeedChart
        synchronization-key="regionOverview"
        :days="7"
        :fixed-time-resolution="1000 * 60 * 60 * 12"
      ></CameraSpeedChart>
    </v-col>

    <v-col
      v-if="canSeeBucketScores"
      :cols="12"
    >
      <GlobalGridScoreChart
        synchronization-key="regionOverview"
        :days="7"
        :fixed-time-resolution="1000 * 60 * 60 * 12"
      ></GlobalGridScoreChart>
    </v-col>
    <v-col
      v-if="canSeeRegionData"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionInfluxChartBuilder({
            chartType: CHART_TYPES.REGIONS,
            regionType: 'stalltechnik',
            whereClauses: { label: allStandingPigs },
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsBarnEquipment_standing_pigs'),
            timeSetup: GeneralChartTimeSetup.lastNDays(7, 'regionOverview', 1000 * 60 * 60 * 12).serialize(),
            regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['fuetterung'] },
            groupByTags: ['pen'],
            xAggregation: 'mean',
          })
        "
      ></GenericInfluxChart>
    </v-col>
    <v-col
      v-if="canSeeRegionData"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionInfluxChartBuilder({
            xAggregation: 'mean',
            regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['fuetterung'] },
            chartType: CHART_TYPES.REGIONS,
            regionType: 'stalltechnik',
            groupByTags: ['pen'],
            whereClauses: { label: allNonStandingPigs },
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsBarnEquipment_non_standing_pigs'),
            timeSetup: GeneralChartTimeSetup.lastNDays(7, 'regionOverview', 1000 * 60 * 60 * 12).serialize(),
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col
      v-if="canSeeRegionData"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionInfluxChartBuilder({
            xAggregation: 'mean',
            regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['traenke'] },
            chartType: CHART_TYPES.REGIONS,
            regionType: 'stalltechnik',
            groupByTags: ['pen'],
            whereClauses: { label: allStandingPigs },
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsBarnEquipment_standing_pigs'),
            timeSetup: GeneralChartTimeSetup.lastNDays(7, 'regionOverview', 1000 * 60 * 60 * 12).serialize(),
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col
      v-if="canSeeRegionData"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionInfluxChartBuilder({
            chartType: CHART_TYPES.REGIONS,
            regionType: 'stalltechnik',
            regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['traenke'] },
            xAggregation: 'mean',
            groupByTags: ['pen'],
            whereClauses: { label: allNonStandingPigs },
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsBarnEquipment_non_standing_pigs'),
            timeSetup: GeneralChartTimeSetup.lastNDays(7, 'regionOverview', 1000 * 60 * 60 * 12).serialize(),
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col
      v-if="canSeeRegionData"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionInfluxChartBuilder({
            chartType: CHART_TYPES.REGIONS,
            regionType: 'funktionsbereich',
            regionSpecTagSelector: {
              tag: 'regionSpec',
              allowEmptyValues: false,
              groupByValues: true,
              useAll: true,
              specificValues: [],
            },
            groupByTags: ['pen'],
            whereClauses: { label: allStandingPigs },
            xAggregation: 'mean',
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsBarnEquipment_standing_pigs'),
            timeSetup: GeneralChartTimeSetup.lastNDays(7, 'regionOverview', 1000 * 60 * 60 * 12).serialize(),
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col
      v-if="canSeeRegionData"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionInfluxChartBuilder({
            chartType: CHART_TYPES.REGIONS,
            regionType: 'funktionsbereich',
            groupByTags: ['pen'],
            whereClauses: { label: allNonStandingPigs },
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsBarnEquipment_non_standing_pigs'),
            timeSetup: GeneralChartTimeSetup.lastNDays(7, 'regionOverview', 1000 * 60 * 60 * 12).serialize(),
            xAggregation: 'mean',
            regionSpecTagSelector: {
              tag: 'regionSpec',
              allowEmptyValues: false,
              groupByValues: true,
              useAll: true,
              specificValues: [],
            },
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col
      v-if="canSeeSpeedData"
      :cols="12"
    >
      <CameraSpeedChart
        :days="7"
        synchronization-key="regionOverview"
      ></CameraSpeedChart>
    </v-col>
    <v-col
      v-if="canSeeImageQualityData"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new IRInfluxChartBuilder({
            timeSetup: GeneralChartTimeSetup.lastNDays(7, 'regionOverview', 1000 * 60).serialize(),
            chartType: CHART_TYPES.IR,
            seriesBasedHeight: true,
            xAggregation: 'first',
            cameraTagSelector: { tag: 'camera', allowEmptyValues: false, groupByValues: true, useAll: true },
            title: $t('charts.titles.nightvision_range_bar'),
          })
        "
      ></GenericInfluxChart>
    </v-col>
    <v-col
      v-if="canSeeBucketScores"
      :cols="12"
    >
      <GlobalGridScoreChart
        synchronization-key="measurementsDashboard"
        :days="7"
      ></GlobalGridScoreChart>
    </v-col>
    <v-col
      v-if="canSeeRegionData"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionInfluxChartBuilder({
            chartType: CHART_TYPES.REGIONS,
            regionType: 'stalltechnik',
            regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['fuetterung'] },
            groupByTags: ['pen'],
            whereClauses: { label: allStandingPigs },
            xAggregation: 'mean',
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsBarnEquipment_standing_pigs'),
            timeSetup: GeneralChartTimeSetup.lastNDays(7, 'regionOverview').serialize(),
          })
        "
      ></GenericInfluxChart>
    </v-col>
    <v-col
      v-if="canSeeRegionData"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionInfluxChartBuilder({
            chartType: CHART_TYPES.REGIONS,
            regionType: 'stalltechnik',
            regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['fuetterung'] },
            groupByTags: ['pen'],
            whereClauses: { label: allNonStandingPigs },
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsBarnEquipment_non_standing_pigs'),
            timeSetup: GeneralChartTimeSetup.lastNDays(7, 'regionOverview').serialize(),
            xAggregation: 'mean',
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col
      v-if="canSeeRegionData"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionInfluxChartBuilder({
            xAggregation: 'mean',
            chartType: CHART_TYPES.REGIONS,
            regionType: 'stalltechnik',
            regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['traenke'] },
            groupByTags: ['pen'],
            whereClauses: { label: allStandingPigs },
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsBarnEquipment_standing_pigs'),
            timeSetup: GeneralChartTimeSetup.lastNDays(7, 'regionOverview').serialize(),
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col
      v-if="canSeeRegionData"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionInfluxChartBuilder({
            xAggregation: 'mean',
            chartType: CHART_TYPES.REGIONS,
            regionType: 'stalltechnik',
            regionSpecTagSelector: { tag: 'regionSpec', allowEmptyValues: false, groupByValues: true, useAll: false, specificValues: ['traenke'] },
            groupByTags: ['pen'],
            whereClauses: { label: allNonStandingPigs },
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsBarnEquipment_non_standing_pigs'),
            timeSetup: GeneralChartTimeSetup.lastNDays(7, 'regionOverview').serialize(),
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col
      v-if="canSeeRegionData"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionInfluxChartBuilder({
            xAggregation: 'mean',
            chartType: CHART_TYPES.REGIONS,
            regionType: 'funktionsbereich',
            regionSpecTagSelector: {
              tag: 'regionSpec',
              allowEmptyValues: false,
              groupByValues: true,
              useAll: true,
              specificValues: [],
            },
            groupByTags: ['pen'],
            whereClauses: { label: allStandingPigs },
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsBarnEquipment_standing_pigs'),
            timeSetup: GeneralChartTimeSetup.lastNDays(7, 'regionOverview').serialize(),
          })
        "
      ></GenericInfluxChart>
    </v-col>

    <v-col
      v-if="canSeeRegionData"
      :cols="12"
    >
      <GenericInfluxChart
        :chart-builder="
          new RegionInfluxChartBuilder({
            xAggregation: 'mean',
            regionSpecTagSelector: {
              tag: 'regionSpec',
              allowEmptyValues: false,
              groupByValues: true,
              useAll: true,
              specificValues: [],
            },
            groupByTags: ['pen'],
            chartType: CHART_TYPES.REGIONS,
            regionType: 'funktionsbereich',
            whereClauses: { label: allNonStandingPigs },
            title: $t('charts.titles.regions'),
            subtitle: $t('charts.subtitles.regionsBarnEquipment_non_standing_pigs'),
            timeSetup: GeneralChartTimeSetup.lastNDays(7, 'regionOverview').serialize(),
          })
        "
      ></GenericInfluxChart>
    </v-col>
  </v-row>
</template>
